import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Pencil1Icon, PlusIcon } from '@radix-ui/react-icons';
import { IconButton } from '@producer/ui-kit';

import { formatDate } from '../../../lib/utils/DateHelper';
import { dateShort } from '../../../assets/contants/dates-fns';
import { timeTo12HourFormat } from '../../../lib/utils/Convertor';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import { InlineTimeSelector } from '../../schedule/components/InlineTimeSelector';

import styles from '../stripboard.module.scss';

export type DayStartStripProps = {
  dayId: string;
  dayName: string;
  dodText: string;
  startDate?: string;
  generalCallTime?: { hour: number; minute: number };
  onChangeTime?: (value: { hour: number; minute: number }) => void;
  onRenameClick?: () => void;
  onAddShot?: () => void;
};

const DayStartStripComponent: React.FC<DayStartStripProps> = observer(
  ({
    dayId,
    dayName,
    dodText,
    startDate,
    onChangeTime,
    onRenameClick,
    onAddShot,
    generalCallTime,
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: `${dayId}-start-strip`,
      data: {
        dayId,
        type: 'row',
      },
      disabled: true,
      animateLayoutChanges: () => true,
      transition: {
        duration: 150,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      },
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const formattedDate = startDate ? formatDate(startDate, dateShort) : '';

    return (
      <div
        className={styles.stickyStrip}
        data-direction="top"
        style={style}
        ref={setNodeRef}
        {...attributes}
        {...listeners}
      >
        <div className={styles.stickyStripContent}>
          <NameWrapper>
            <DayName>{dayName}</DayName>

            <IconButton
              icon={<Pencil1Icon color="var(--colors-texts-high-contrast)" />}
              onClick={onRenameClick}
            />
          </NameWrapper>

          <CenterWrapper>
            <LeftWrapper>{formattedDate && <DayText>{formattedDate}</DayText>}</LeftWrapper>

            {generalCallTime && (
              <InlineTimeSelector
                time={timeTo12HourFormat(generalCallTime)}
                textToDisplay="General call time "
                backgroundVisible
                style={{ margin: '0' }}
                onChange={onChangeTime}
              />
            )}

            <RightWrapper>
              <DodText>{dodText}</DodText>
            </RightWrapper>
          </CenterWrapper>

          <div className={styles.addShotButton}>
            <Tooltip text="Add shot">
              <IconButton
                icon={<PlusIcon />}
                onClick={onAddShot}
              />
            </Tooltip>
          </div>
        </div>
      </div>
    );
  },
);

export const DayStartStrip = React.memo(DayStartStripComponent);

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  grid-column: 1 / span 3;
`;

const DayName = styled.h3`
  font-weight: 400;
  font-size: 1.6rem;
  color: var(--color-texts-high-contrast);
  line-height: 2.4rem;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CenterWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 1.6rem;
  justify-content: center;
`;

const RightWrapper = styled.div`
  position: absolute;
  min-width: max-content;
  margin-inline-start: 1.6rem;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
`;

const LeftWrapper = styled.div`
  position: absolute;
  min-width: max-content;
  margin-inline-end: 1.6rem;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
`;

const DayText = styled.p`
  color: var(--color-texts-high-contrast);
  line-height: 1.8rem;
`;

const DodText = styled(DayText)`
  @media screen and (max-width: 1130px) {
    padding-left: 0.8rem;
  }
`;
